import { create } from 'zustand';

import type { View } from '../../../types/forge';

/**
 * Builder editor state — selection, dirty buffer, drag state.
 *
 * Mirrors Burrow's `InterfaceController`. Single-selection only in Phase 2.
 */
export interface EditorState {
  appId: string | null;
  selectedViewId: string | null;
  /** Buffered (unsaved) edits to the selected view, keyed by view id */
  dirtyById: Record<string, Partial<View>>;
  changesNotSaved: boolean;

  /** Drag-from-palette state */
  draggedViewType: string | null;
  hoveredParentId: string | null;
  dropIndex: number;

  /** Right-panel tab */
  rightPanel: 'properties' | 'styles' | 'interactions';

  setAppId: (appId: string | null) => void;
  selectView: (viewId: string | null) => void;
  bufferEdit: (viewId: string, patch: Partial<View>) => void;
  clearBuffer: (viewId: string) => void;
  startDrag: (type: string) => void;
  setDropTarget: (parentId: string | null, index: number) => void;
  endDrag: () => void;
  setRightPanel: (tab: EditorState['rightPanel']) => void;
}

export const useEditorStore = create<EditorState>((set) => ({
  appId: null,
  selectedViewId: null,
  dirtyById: {},
  changesNotSaved: false,
  draggedViewType: null,
  hoveredParentId: null,
  dropIndex: 0,
  rightPanel: 'properties',

  setAppId(appId) {
    set({ appId, selectedViewId: null, dirtyById: {}, changesNotSaved: false });
  },
  selectView(viewId) {
    set({ selectedViewId: viewId });
  },
  bufferEdit(viewId, patch) {
    set((state) => ({
      dirtyById: { ...state.dirtyById, [viewId]: { ...state.dirtyById[viewId], ...patch } },
      changesNotSaved: true,
    }));
  },
  clearBuffer(viewId) {
    set((state) => {
      const next = { ...state.dirtyById };
      delete next[viewId];
      return { dirtyById: next, changesNotSaved: Object.keys(next).length > 0 };
    });
  },
  startDrag(type) {
    set({ draggedViewType: type });
  },
  setDropTarget(parentId, index) {
    set({ hoveredParentId: parentId, dropIndex: index });
  },
  endDrag() {
    set({ draggedViewType: null, hoveredParentId: null, dropIndex: 0 });
  },
  setRightPanel(tab) {
    set({ rightPanel: tab });
  },
}));
